import { isHttpError } from "@akashnetwork/http-sdk";
import type { LoggerService } from "@akashnetwork/logging";
import { isApiError } from "@akashnetwork/openapi-sdk";
import type { SeverityLevel as SentrySeverityLevel } from "@sentry/nextjs";
import { captureException as captureExceptionInSentry, getTraceData } from "@sentry/nextjs";

const EDGE_REFUSAL_STATUSES = [403, 429];

const EDGE_REFUSAL_FINGERPRINT = ["edge-refusal"];

const HTML_DOCUMENT = /<html[\s>]/i;

export class ErrorHandlerService {
  /** The CDN refuses every request of a page it blocks, so the first refusal says all the others would. */
  #hasReportedEdgeRefusal = false;

  constructor(
    private readonly logger: LoggerService,
    private readonly captureException = captureExceptionInSentry
  ) {}

  getTraceData(): TraceData {
    const data = getTraceData();
    const traceId = data["sentry-trace"];

    return {
      traceId,
      traceIdW3C: sentryTraceToW3C(traceId),
      baggage: data.baggage
    };
  }

  reportError({ severity, error, tags, ...extra }: ErrorContext): void {
    if (isCancellation(error)) {
      return;
    }

    const isRefusedAtEdge = isEdgeRefusal(error);
    if (isRefusedAtEdge) {
      if (this.#hasReportedEdgeRefusal) return;
      this.#hasReportedEdgeRefusal = true;
    }

    const finalTags: Record<string, string> = { ...tags };

    if (isHttpError(error) && error.response) {
      finalTags.status = error.response.status.toString();
      finalTags.method = error.response.config.method?.toUpperCase() || "UNKNOWN";
      finalTags.url = error.response.config.url || "UNKNOWN";
      extra.headers = error.response.headers;
    }

    this.logger.error({ ...extra, ...finalTags, error });
    this.captureException(error, {
      level: severity || "error",
      extra,
      tags: finalTags,
      ...(isRefusedAtEdge && { fingerprint: EDGE_REFUSAL_FINGERPRINT })
    });
  }

  wrapCallback<T extends (...args: any[]) => any>(fn: T, options?: WrapCallbackOptions<ReturnType<T>>): T {
    return ((...args) => {
      try {
        const result = fn(...args);
        if (result && typeof result.catch === "function") {
          return result.catch((error: unknown) => {
            this.reportError({ error, tags: options?.tags });
            if (options?.fallbackValue) return options.fallbackValue();
          });
        }
        return result;
      } catch (error) {
        this.reportError({ error, tags: options?.tags });
        if (options?.fallbackValue) return options.fallbackValue();
      }
    }) as T;
  }
}

/** Monaco names its cancellations `Canceled` and axios uses `CanceledError`; a cancelled request is not a fault. */
function isCancellation(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const { name, code } = error as { name?: unknown; code?: unknown };
  return name === "AbortError" || name === "CanceledError" || name === "Canceled" || code === "ERR_CANCELED";
}

/** Console's API answers in JSON, so a 403 or 429 that carries an HTML page was refused by the CDN before reaching Console. */
function isEdgeRefusal(error: unknown): boolean {
  if (isApiError(error)) return EDGE_REFUSAL_STATUSES.includes(error.status) && typeof error.body === "string" && HTML_DOCUMENT.test(error.body);

  return (
    isHttpError(error) &&
    !!error.response &&
    EDGE_REFUSAL_STATUSES.includes(error.response.status) &&
    String(error.response.headers["content-type"]).includes("text/html")
  );
}

export type SeverityLevel = SentrySeverityLevel;

export interface ErrorContext {
  [key: string]: unknown;
  severity?: SeverityLevel;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

export interface WrapCallbackOptions<TValue> {
  tags?: Record<string, string>;
  fallbackValue?: () => TValue;
}

export interface TraceData {
  traceId?: string;
  traceIdW3C?: string;
  baggage?: string;
}

/**
 * Converts Sentry `sentry-trace` header value into a valid W3C `traceparent`.
 *
 * Sentry: `<traceId>-<spanId>-<sampled>` where sampled is `0|1` (optional).
 * W3C:    `00-<traceId>-<parentSpanId>-<flags>` where flags is 2 hex chars (`00|01`).
 */
export function sentryTraceToW3C(sentryTrace?: string): string | undefined {
  const value = sentryTrace?.trim();
  if (!value) return;

  const [traceId, spanId, sampled] = value.split("-", 3);
  // W3C spec disallows all-zero ids
  if (!traceId || !spanId || Number(traceId) === 0 || Number(spanId) === 0) return;

  const flags = Number(sampled) === 1 ? "01" : "00";
  return `00-${traceId}-${spanId}-${flags}`;
}
